import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  FiArrowLeft,
  FiCalendar,
  FiRefreshCw,
  FiLayers,
  FiTrendingDown,
  FiClock,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getCurrentFinancialYear, getFinancialYearLabel } from '../../utils/istDate';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const TOTAL_ANNUAL_DAYS = 24;

export default function EmpLeaveBreakdownPage({
  isEmbedded = false,
  selectedYear: propYear,
  onBack,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const initialYear =
    propYear || location.state?.selectedYear || getCurrentFinancialYear();
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState({
    annualLimit: TOTAL_ANNUAL_DAYS,
    financialYear: selectedYear,
    financialYearLabel: '',
    totalCarryForward: 0,
    carryForward: 0,
    carriedForward: 0,
    earnedLeave: 0,
    totalTakenYear: 0,
    paidTakenYear: 0,
    totalPaidLeavesAvailableYear: TOTAL_ANNUAL_DAYS,
    currentAvailableBalance: TOTAL_ANNUAL_DAYS,
    encashedDays: 0,
    lopLeavesYear: 0,
    currentMonthAccrual: 2,
    currentYearAccrued: 0,
    cumulativeAvailable: TOTAL_ANNUAL_DAYS,
    monthlyPaidLeaveLimit: 3,
    paidTakenMonth: 0,
    totalTakenMonth: 0,
    totalPaidLeavesAvailableMonth: 0,
    lopLeavesMonth: 0,
    lopDeductionAmount: null,
    lopDeductionNote: null,
  });

  // Sync propYear if provided
  useEffect(() => {
    if (propYear && propYear !== selectedYear) {
      setSelectedYear(propYear);
    }
  }, [propYear]);

  const fetchBalance = useCallback(
    async (yearToFetch = selectedYear) => {
      setLoading(true);
      try {
        const res = await api.get(`/leave/monthly-balance/${yearToFetch}`);
        const data = res.data?.data || res.data;
        if (data) {
          setBalance(data);
        }
      } catch (err) {
        console.error('Failed to load leave breakdown:', err);
        toast.error('Could not load leave breakdown data');
      } finally {
        setLoading(false);
      }
    },
    [selectedYear]
  );

  useEffect(() => {
    fetchBalance(selectedYear);
  }, [selectedYear, fetchBalance]);

  const fyLabel =
    balance.financialYearLabel ||
    `FY ${selectedYear}–${String(Number(selectedYear) + 1).slice(-2)}`;
  // Carry forward leaves does not include current month leaves (carry forward - 2, never < 0)
  const rawCarryFwd =
    balance.carriedForward ??
    balance.totalCarryForward ??
    balance.carryForward ??
    balance.cumulativeAvailable ??
    0;
  const carryFwd = Math.max(0, Number(rawCarryFwd || 0) - 2);
  const earnedLv = balance.earnedLeave ?? 0;
  const baseEntitlement = balance.annualLimit ?? TOTAL_ANNUAL_DAYS;
  const totalAnnualPool = baseEntitlement + carryFwd + earnedLv;

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/leaves-wfh');
    }
  };

  return (
    <div className="space-y-6 pb-6 max-w-7xl mx-auto">
      {/* ── Top Header Bar (Only shown in standalone page mode) ── */}
      {!isEmbedded && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-blue-600 transition-colors mb-2 cursor-pointer"
            >
              <FiArrowLeft size={14} /> Back to Leave Summary
            </button>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
                <FiLayers className="text-blue-600" /> Leave Quota &amp; Breakdown
              </h1>
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                {fyLabel}
              </span>
            </div>
            <p className="text-xs text-slate-500 font-medium mt-1">
              Complete statement of annual quota, monthly accruals, carry-forward &amp; utilization
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <div className="flex-1 sm:flex-initial flex items-center justify-between sm:justify-start gap-2 bg-white border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-xs">
              <span className="flex items-center gap-1.5 text-slate-400 font-medium">
                <FiCalendar className="text-slate-400" /> Fiscal Year:
              </span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                disabled={loading}
                className="bg-transparent font-bold text-blue-600 outline-none cursor-pointer"
              >
                {[2024, 2025, 2026, 2027].map((fy) => (
                  <option key={fy} value={fy}>
                    {getFinancialYearLabel(fy)}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => fetchBalance(selectedYear)}
              disabled={loading}
              className="flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors cursor-pointer shadow-xs disabled:opacity-50 shrink-0"
              title="Refresh details"
            >
              <FiRefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      )}

      {/* ── 4 Key KPI Metrics Cards (Only in standalone page mode) ── */}
      {!isEmbedded && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
          {/* Metric 1 */}
          <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex flex-col justify-between shadow-xs">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">
              Total Annual Pool
            </span>
            <div className="my-1 sm:my-1.5">
              <span className="text-2xl sm:text-3xl font-black text-slate-800 leading-none">
                {totalAnnualPool}
              </span>
            </div>
            <span className="text-xs text-slate-400">days</span>
          </div>

          {/* Metric 2 */}
          <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex flex-col justify-between shadow-xs">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">
              Accrued To Date
            </span>
            <div className="my-1 sm:my-1.5">
              <span className="text-2xl sm:text-3xl font-black text-slate-800 leading-none">
                {balance.currentYearAccrued ?? 0}
              </span>
            </div>
            <span className="text-xs text-slate-400">days</span>
          </div>

          {/* Metric 3 */}
          <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex flex-col justify-between shadow-xs">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">
              Available Balance
            </span>
            <div className="my-1 sm:my-1.5">
              <span className="text-2xl sm:text-3xl font-black text-slate-800 leading-none">
                {balance.cumulativeAvailable ?? balance.currentAvailableBalance ?? 0}
              </span>
            </div>
            <span className="text-xs text-slate-400">days</span>
          </div>

          {/* Metric 4 */}
          <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex flex-col justify-between shadow-xs">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">
              Remaining (Year)
            </span>
            <div className="my-1 sm:my-1.5">
              <span className="text-2xl sm:text-3xl font-black text-blue-600 leading-none">
                {balance.totalPaidLeavesAvailableYear ?? balance.currentAvailableBalance ?? 0}
              </span>
            </div>
            <span className="text-xs text-slate-400">days</span>
          </div>
        </div>
      )}

      {/* ── Two Symmetrical Ledgers (Annual vs Monthly) ── */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 h-80 animate-pulse" />
          <div className="bg-white rounded-2xl border border-slate-200 p-8 h-80 animate-pulse" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* ── Annual Breakdown ── */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                  <FiLayers size={16} />
                </div>
                <h3 className="text-sm font-bold text-slate-900">
                  Annual Breakdown
                </h3>
              </div>
              <span className="text-xs font-bold text-slate-500 font-mono">
                {fyLabel}
              </span>
            </div>

            {/* Rows */}
            <div className="divide-y divide-slate-100 flex-1 text-xs">
              <div className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/40 transition-colors">
                <span className="font-semibold text-slate-800">
                  Base Annual Entitlement
                </span>
                <span className="font-bold text-slate-800 font-mono text-sm">
                  {baseEntitlement} days
                </span>
              </div>

              <div className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/40 transition-colors">
                <span className="font-semibold text-slate-800">
                  Carried Forward
                </span>
                <span className="font-bold text-slate-700 font-mono text-sm">
                  {carryFwd} days
                </span>
              </div>

              <div className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/40 transition-colors">
                <span className="font-semibold text-slate-800">
                  Earned Leaves (This Year)
                </span>
                <span className="font-bold text-slate-700 font-mono text-sm">
                  {earnedLv} days
                </span>
              </div>

              <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50/60">
                <span className="font-bold text-slate-800">
                  Total Annual Leaves
                </span>
                <span className="font-black text-slate-900 font-mono text-sm">
                  {totalAnnualPool} days
                </span>
              </div>

              <div className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/40 transition-colors">
                <span className="font-semibold text-slate-800">
                  Paid Leaves Taken
                </span>
                <span className="font-bold text-slate-700 font-mono text-sm">
                  {balance.paidTakenYear ?? 0} days
                </span>
              </div>

              <div className="flex items-center justify-between px-5 py-3.5 bg-blue-50/30">
                <span className="font-bold text-blue-900">
                  Remaining Balance
                </span>
                <span className="font-black text-blue-700 font-mono text-sm">
                  {balance.totalPaidLeavesAvailableYear ?? balance.currentAvailableBalance ?? 0} days
                </span>
              </div>
            </div>
          </div>

          {/* ── Monthly Breakdown ── */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center font-bold">
                  <FiClock size={16} />
                </div>
                <h3 className="text-sm font-bold text-slate-900">
                  Monthly Breakdown
                </h3>
              </div>
              <span className="text-xs font-semibold text-slate-500">
                Accrues 2 days / month
              </span>
            </div>

            {/* Rows */}
            <div className="divide-y divide-slate-100 flex-1 text-xs">
              <div className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/40 transition-colors">
                <span className="font-semibold text-slate-800">
                  Accrued Up To This Month
                </span>
                <span className="font-bold text-slate-800 font-mono text-sm">
                  {balance.currentYearAccrued ?? 0} days
                </span>
              </div>

              <div className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/40 transition-colors">
                <span className="font-semibold text-slate-800">
                  Available Balance
                </span>
                <span className="font-bold text-slate-800 font-mono text-sm">
                  {balance.cumulativeAvailable ?? balance.currentAvailableBalance ?? 0} days
                </span>
              </div>

              <div className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/40 transition-colors">
                <span className="font-semibold text-slate-800">
                  Monthly Limit
                </span>
                <span className="font-bold text-slate-700 font-mono text-sm">
                  {balance.monthlyPaidLeaveLimit ?? 3} days
                </span>
              </div>

              <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50/60">
                <span className="font-bold text-slate-800">
                  Paid Leaves Taken (This Month)
                </span>
                <span className="font-black text-slate-900 font-mono text-sm">
                  {balance.paidTakenMonth ?? 0} days
                </span>
              </div>

              <div className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/40 transition-colors">
                <span className="font-semibold text-slate-800">
                  Total Leaves Taken (This Month)
                </span>
                <span className="font-bold text-slate-700 font-mono text-sm">
                  {(balance.paidTakenMonth ?? 0) + (balance.lopLeavesMonth ?? 0)} days
                </span>
              </div>

              <div className="flex items-center justify-between px-5 py-3.5 bg-blue-50/30">
                <span className="font-bold text-blue-900">
                  Remaining Leaves (This Month)
                </span>
                <span className="font-black text-blue-700 font-mono text-sm">
                  {balance.totalPaidLeavesAvailableMonth ?? 0} days
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Loss of Pay (LOP) Summary ── */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
              <FiTrendingDown size={17} />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <span>Loss of Pay (LOP)</span>
              </h4>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:gap-2.5 w-full sm:w-auto">
            <div className="px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-center min-w-[90px]">
              <span className="block text-[9px] font-bold uppercase tracking-wider text-slate-400">
                This Month
              </span>
              <span className="text-sm font-black text-slate-800">
                {balance.lopLeavesMonth ?? 0} days
              </span>
            </div>

            <div className="px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-center min-w-[90px]">
              <span className="block text-[9px] font-bold uppercase tracking-wider text-slate-400">
                This Year
              </span>
              <span className="text-sm font-black text-slate-800">
                {balance.lopLeavesYear ?? 0} days
              </span>
            </div>

            {balance.lopDeductionAmount != null && (
              <div className="col-span-2 sm:col-span-1 px-3 py-2 rounded-xl border border-rose-200 bg-rose-50/50 text-center min-w-[110px]">
                <span className="block text-[9px] font-bold uppercase tracking-wider text-rose-600">
                  Deduction
                </span>
                <span className="text-sm font-black text-rose-700">
                  ₹{Number(balance.lopDeductionAmount).toLocaleString('en-IN')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
